import { pictureUrl } from "./orderDocumentApi";

/*
 * Черновик бланка на сайте: из ответа сервера (document + form) → поля редактора,
 * и обратно → что отправляем на сервер. Общий для документа заказа
 * (OrderDocumentPanel) и бланка до заказа в 3D-калькуляторе (ScenePrintForm).
 */

export const toDateInput = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Эскиз с сервера (уже с подстановками из заказа) → черновик для редактора. */
export const blankFrom = (blank = {}) => ({
    title: blank.title || "",
    article: blank.article || "",
    fences: blank.fences && typeof blank.fences === "object" ? blank.fences : {},
    contractNumber: blank.contractNumber || "",
    contractDate: blank.contractDate || "",
    messengerPhone: blank.messengerPhone || "",
    front: {
        image: blank.front?.image || { type: "none", id: "" },
        overlay: !!blank.front?.overlay,
        overlayPos: blank.front?.overlayPos || "center",
        overlayColor: blank.front?.overlayColor || "#ffffff",
        photoZoom: blank.front?.photoZoom ?? 1,
        photoX: blank.front?.photoX ?? 50,
        photoY: blank.front?.photoY ?? 50,
        textScale: blank.front?.textScale ?? 1,
        textY: blank.front?.textY ?? 38,
        textWidth: blank.front?.textWidth ?? 90,
        textX: blank.front?.textX ?? 0,
        font: blank.front?.font || "roboto",
        epitaphFont: blank.front?.epitaphFont || "",
        ...["surnameSize", "nameSize", "datesSize", "fioLine", "fioLetter", "datesLine", "datesLetter", "epitaphSize", "epitaphLine", "epitaphLetter"]
            .reduce((acc, key) => (blank.front?.[key] === undefined ? acc : { ...acc, [key]: blank.front[key] }), {}),
        text: blank.front?.text || "",
    },
    back: {
        title: blank.back?.title || "Оборотная сторона",
        image: blank.back?.image || { type: "none", id: "" },
        photoZoom: blank.back?.photoZoom ?? 1,
        photoX: blank.back?.photoX ?? 50,
        photoY: blank.back?.photoY ?? 50,
        textScale: blank.back?.textScale ?? 1,
        textY: blank.back?.textY ?? 50,
        textX: blank.back?.textX ?? 0,
        text: blank.back?.text || "",
    },
    backEnabled: !!blank.backEnabled,
    epitaphSide: blank.epitaphSide === "back" ? "back" : "front",
    persons: ["single", "double", "triple"].includes(blank.persons) ? blank.persons : "single",
    sharedSurnameEnabled: blank.sharedSurnameEnabled === true,
    sharedSurname: blank.sharedSurname || "",
    extraPersons: Array.isArray(blank.extraPersons) ? blank.extraPersons : [],
    components: Array.isArray(blank.components) ? blank.components.map((row) => ({
        name: row.name || "",
        size: row.size || "",
        polish: row.polish || "",
        note: row.note || "",
    })) : [],
    checks: { ...(blank.checks || {}) },
    options: { ...(blank.options || {}) },
    extras: Array.isArray(blank.extras) ? blank.extras : [],
    installPeriod: blank.installPeriod || "",
    installationTemplateId: blank.installationTemplateId || "",
    installPlace: blank.installPlace || "",
    deliveryPlace: blank.deliveryPlace || "",
    showMonth: blank.showMonth || "",
    showPrices: blank.showPrices !== false,
    priceOverrides: blank.priceOverrides && typeof blank.priceOverrides === "object" ? { ...blank.priceOverrides } : {},
    priceOverrideRows: blank.priceOverrideRows && typeof blank.priceOverrideRows === "object" ? { ...blank.priceOverrideRows } : {},
});

/** Фото товара на стороне памятника — с адресом, по которому сервер найдёт файл. */
const withSrc = (ref, items = []) => {
    if (ref?.type !== "product") return ref;
    const item = items.find((entry) => String(entry.n) === String(ref.id));
    return item?.picture ? { ...ref, src: pictureUrl(item.picture) } : ref;
};

/** Что отправляем на сервер (сохранение и предпросмотр). */
export const payloadOf = (draft, withBlank = true, items = []) => ({
    overrides: draft.overrides,
    deceased: draft.deceased,
    installationAt: draft.installationAt || null,
    prepaymentAmount: draft.prepaymentAmount === "" ? null : Number(String(draft.prepaymentAmount).replace(",", ".")),
    termsText: draft.termsText,
    acceptedBy: draft.acceptedBy,
    layout: draft.layout,
    sceneShotIds: draft.sceneShotIds,
    ...(draft.checkout ? { checkout: draft.checkout } : {}),
    ...(withBlank ? {
        blank: {
            ...draft.blank,
            front: { ...draft.blank.front, image: withSrc(draft.blank.front.image, items) },
            back: { ...draft.blank.back, image: withSrc(draft.blank.back.image, items) },
            extras: draft.blank.extras.filter((line) => line.trim()),
        },
    } : {}),
});

export const draftFrom = (form = {}, document = {}) => ({
    overrides: {
        customerName: form.overrides?.customerName || "",
        customerPhone: form.overrides?.customerPhone || "",
        customerEmail: form.overrides?.customerEmail || "",
        customerAddress: form.overrides?.customerAddress || "",
        cemeteryName: form.overrides?.cemeteryName || "",
        sector: form.overrides?.sector || "",
        plot: form.overrides?.plot || "",
        burialPlace: form.overrides?.burialPlace || "",
        comment: form.overrides?.comment || "",
    },
    // Надпись: что сохранено в бланке; пусто — что сервер взял из 3D-сцены.
    deceased: (() => {
        const saved = form.deceased || {};
        const hasSaved = ["surname", "name", "patronymic", "dates", "epitaph"].some((key) => String(saved[key] || "").trim());
        const source = hasSaved ? saved : (document.deceased || {});
        return {
            surname: source.surname || "",
            name: source.name || "",
            patronymic: source.patronymic || "",
            dates: source.dates || "",
            epitaph: source.epitaph || "",
            portraitInfo: saved.portraitInfo || source.portraitInfo || "",
        };
    })(),
    installationAt: toDateInput(form.installationAt),
    prepaymentAmount: form.prepaymentAmount === null || form.prepaymentAmount === undefined ? "" : String(form.prepaymentAmount),
    termsText: form.termsText || "",
    acceptedBy: form.acceptedBy || "",
    layout: form.layout || {},
    sceneShotIds: Array.isArray(form.sceneShotIds) ? form.sceneShotIds : [],
    blank: blankFrom(document.blank),
    blankTouched: false,
});
